import { useState, useCallback, useEffect } from 'react';

interface FullscreenDocument extends Document {
  webkitFullscreenElement?: Element | null;
  msFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void>;
  msExitFullscreen?: () => Promise<void>;
}

export const useFullScreen = (onExitCallback?: () => void) => {
  const [isFullScreen, setIsFullScreen] = useState(false);

  const requestFullScreen = useCallback((elementId: string) => {
    const element = document.getElementById(elementId) || document.querySelector(elementId);

    if (element) {
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if ((element as any).webkitRequestFullscreen) {
        (element as any).webkitRequestFullscreen();
      } else if ((element as any).msRequestFullscreen) {
        (element as any).msRequestFullscreen();
      } else {
        console.error('Fullscreen API is not supported.');
      }
    } else {
      console.error(`Element with ID or selector '${elementId}' not found.`);
    }
  }, []);

  const exitFullScreen = useCallback(() => {
    const doc = document as FullscreenDocument;
    if (document.fullscreenElement || doc.webkitFullscreenElement || doc.msFullscreenElement) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (doc.webkitExitFullscreen) {
        doc.webkitExitFullscreen();
      } else if (doc.msExitFullscreen) {
        doc.msExitFullscreen();
      }
    }
  }, []);

  useEffect(() => {
    if (!onExitCallback) return;
    const handleFullScreenChange = () => {
      const isCurrentlyFullScreen = Boolean(
        document.fullscreenElement ||
          (document as FullscreenDocument).webkitFullscreenElement ||
          (document as FullscreenDocument).msFullscreenElement
      );
      setIsFullScreen(isCurrentlyFullScreen);

      if (!isCurrentlyFullScreen && onExitCallback) {
        onExitCallback();
      }
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullScreenChange);
    document.addEventListener('msfullscreenchange', handleFullScreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullScreenChange);
      document.removeEventListener('msfullscreenchange', handleFullScreenChange);
    };
  }, [onExitCallback]);

  return { isFullScreen, requestFullScreen, exitFullScreen };
};
